import { Drawer, Form, Input, Select, message } from "antd";
import { useEffect, useState } from "react";

import TableCustom from "../../../components/ui/TableCustom";
import { Colors } from "../../../constants/Colors";
import DrawerFooter from "../../../components/layout/drawer/DrawerFooter";
import requiredRule from "../../../utils/formRules/requiredRule";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import { daysInMonth, monthMap } from "./content/content";
import { SelectOption, ThemeType } from "./content/dailyCheckInTypes";
import createThemeColumns from "./content/themeColumns";
import { fetchThemesList, updateTheme } from "./services/dailyCheckIn.services";

const ThemeTabContent = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [startOptions, setStartOptions] = useState<SelectOption[]>([]);
  const [endOptions, setEndOptions] = useState<SelectOption[]>([]);
  const [displayData, setDisplayData] = useState<ThemeType[] | any>([]);
  const [editData, setEditData] = useState<ThemeType | undefined>(undefined);
  const [form] = Form.useForm();

  const handleEditClick = (record: ThemeType) => {
    setShowDrawer(true);
    setEditData(record);
  };

  const getThemesList = async () => {
    const data = await fetchThemesList();

    if (data) setDisplayData(data.data);
  }

  const getMonthNumber = (monthAbbr: string) => {
    if (!monthAbbr) return "01";

    return Object.entries(monthMap).find(([num, abbr]) => abbr === monthAbbr.slice(0, 3))?.[0] || "01";
  };

  const generateMonthOptions = (month: string, startDay = 1) => {
    const days = daysInMonth[month];
    const options = [];
    for (let d = startDay; d <= days; d++) {
      const dayStr = d.toString().padStart(2, '0');
      options.push({
        value: `${month}-${dayStr}`,
        label: `${monthMap[month]} ${dayStr}`,
      });
    }
    return options;
  };

  const handleClose = () => {
    form.resetFields();
    setEditData(undefined);
    setShowDrawer(false);
  }

  const handleSubmit = async (values: any) => {
    const payload = {
      themeId: values.themeId,
      monthDayStart: values.monthDayStart,
      monthDayEnd: values.monthDayEnd,
      themeName: values.themeName,
    };

    const data = await updateTheme(payload);

    if (data?.status === "SUCCESS") {
      getThemesList();
      handleClose();
      message.success(renderAntDMessageConfig("Update successful"));
    } else {
      message.error(renderAntDMessageConfig("Update failed"));
    }
  };

  const handleStartDateChange = (value: any) => {
    const month = value.split("-")[0];
    const day = parseInt(value.split("-")[1], 10);
    setEndOptions(generateMonthOptions(month, day));
    form.setFieldsValue({ monthDayEnd: undefined });
  };

  useEffect(() => {
    getThemesList();
  }, []);

  useEffect(() => {
    if (editData) {
      const month = getMonthNumber(editData.monthText);
      const startDay = parseInt(editData.monthDayStart.split("-")[1], 10);

      setStartOptions(generateMonthOptions(month));
      setEndOptions(generateMonthOptions(month, startDay));

      form.setFieldsValue({
        themeId: editData.themeId,
        monthText: editData.monthText,
        themeSlot: editData.themeSlot,
        monthDayStart: editData.monthDayStart,
        monthDayEnd: editData.monthDayEnd,
        themeName: editData.themeName,
        albumSubtitleId: editData.albumSubtitleId,
      });
    }
  }, [editData, form]);

  return (
    <>
      <div className="table-container">
        <TableCustom
          rowKey={(record: ThemeType) => record.themeId}
          columns={createThemeColumns(handleEditClick)}
          dataSource={displayData}
          pagination={false}
        />
      </div>
      <div id="themeTabContentWrapper">
        <Drawer
          title="Edit the theme"
          open={showDrawer}
          onClose={() => handleClose()}
          width={500}
        >
          <Form
            name="theme-form"
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
          >
            <Form.Item
              name="themeId"
              hidden
            >
              <Input />
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Month"
              name="monthText"
            >
              <p style={{ marginTop: -16, color: Colors.notEditableText }}>
                {editData?.monthText}
              </p>
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Theme slot"
              name="themeSlot"
            >
              <p style={{ marginTop: -16, color: Colors.notEditableText }}>
                {editData?.themeSlot}
              </p>
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Start date"
              name="monthDayStart"
            >
              <Select options={startOptions} disabled={true} onChange={(value) => handleStartDateChange(value)} />
            </Form.Item>
            <Form.Item
              className="form-item"
              label="End date"
              name="monthDayEnd"
            >
              <Select options={endOptions} disabled={true} />
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Theme name"
              name="themeName"
              rules={[requiredRule]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              className="form-item"
              label="EDWZ Video ID"
              name="albumSubtitleId"
            >
              <Input disabled={true} />
            </Form.Item>
            <DrawerFooter
              handleCancel={() => {
                form.resetFields();
                setEditData(undefined);
                setShowDrawer(false);
              }}
            />
          </Form>
        </Drawer>
      </div>
    </>
  );
};

export default ThemeTabContent;
